import React, { useEffect, useMemo, useRef } from 'react';
import classes from './ModalAddCredential.module.scss';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { useSelector } from 'react-redux';
import { ModalConfig } from 'new/routes/RouterManager';
import { Table as MuiTable, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { PROBE_GET_SAMPLE_REQUEST } from 'js/constants/actionTypes';
import cx from 'classnames';
import Typography from 'new/elements/Typography';
import DestinationPayloadContainer from 'js/components/destination/destination-content/DestinationPayloadContainer';
import { CONNECTION_CATEGORY_NOSQL } from 'js/constants/connectionCategories';
import { isArray, isObject } from 'lodash';

interface ModalPreviewSamplesProps {
  credentialId?: number;
  connectionCategory: string;
}

type Samples = {
  data: any[];
  columns: string[];
};

export const ModalPreviewSamples = (props: ModalPreviewSamplesProps & Partial<ModalOutProps>) => {
  const { setLoading, connectionCategory, credentialId } = props;
  const probe = useSelector((state: any) => state.probe);
  const output = probe[credentialId]?.[0]?.response?.output;
  const outputSamples: Samples = output?.response;
  const prevSamplesRef = useRef(outputSamples);
  const errorMessage: string = output?.errorMessage;
  const loading = useIsLoading(PROBE_GET_SAMPLE_REQUEST);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  const samples = useMemo(() => {
    if (!!outputSamples) {
      prevSamplesRef.current = outputSamples;
      return outputSamples;
    }
    return prevSamplesRef.current;
  }, [outputSamples]);

  const rowKeys = useMemo(() => {
    const keyByIndex = {};
    samples?.data?.forEach((row, index) => {
      keyByIndex[index] = samples?.columns?.reduce(
        (key, column) => `${key}${row[column]}-${index}`,
        '',
      );
    });
    return keyByIndex;
  }, [samples]);

  return (
    <div className={classes.modalContainer}>
      {!!errorMessage && connectionCategory !== CONNECTION_CATEGORY_NOSQL ? (
        <Typography variant="h5">{errorMessage}</Typography>
      ) : connectionCategory === CONNECTION_CATEGORY_NOSQL ? (
        <DestinationPayloadContainer panelHeading="Sample Document" disableTabs />
      ) : (
        <MuiTable size="small" className={cx(classes.table)}>
          <TableHead>
            <TableRow>
              {samples?.columns?.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {samples?.data?.map((row, dIndex) => (
              <TableRow key={rowKeys[dIndex]}>
                {samples?.columns?.map((column, cIndex) => (
                  <TableCell key={`${row[column]}-${dIndex}-${cIndex}`} component="th" scope="row">
                    {isObject(row[column]) || isArray(row[column])
                      ? JSON.stringify(row[column])
                      : row[column]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      )}
    </div>
  );
};

ModalPreviewSamples.modalConfig = {
  name: 'ModalPreviewSamples',
  title: 'Preview samples',
} as ModalConfig;
